import type { ManualInfo } from '../api/types'

const x1 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 })
const x0 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 })
const x2 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 })

/** ×12,4 — русская десятичная запятая; от ×100 без дробной части. */
export function formatX(value: number): string {
  return `×${(Math.abs(value) >= 100 ? x0 : x1).format(value)}`
}

export type XLevel = 'x1' | 'x2' | 'x3' | 'x5'

/** ×2–3 янтарный, ×3–5 коралловый, ×5+ красный, меньше ×2 нейтральный. */
export function xLevel(value: number): XLevel {
  if (value >= 5) return 'x5'
  if (value >= 3) return 'x3'
  if (value >= 2) return 'x2'
  return 'x1'
}

/** 310 → «310», 12 400 → «12,4 тыс.», 1 250 000 → «1,3 млн». Неразрывный пробел перед единицей. */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const abs = Math.abs(value)
  if (abs < 1000) return x0.format(value)
  if (abs < 1_000_000) {
    // 999 950 → «1 млн», а не «1 000 тыс.»
    const k = Math.round(value / 100) / 10
    if (Math.abs(k) < 1000) return `${(Math.abs(k) >= 100 ? x0 : x1).format(k)} тыс.`
  }
  return `${x1.format(value / 1_000_000)} млн`
}

/** Доля → проценты: две значащие у малых, одна дробная у больших. 0,0123 → «1,2 %». */
export function formatPct(share: number | null | undefined): string {
  if (share === null || share === undefined) return '—'
  const p = share * 100
  return `${(p < 1 ? x2 : x1).format(p)} %`
}

export function formatAge(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return ''
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const minutes = Math.max(0, (now - t) / 60_000)
  if (minutes < 1) return 'только что'
  if (minutes < 60) return `${Math.floor(minutes)} мин`
  const hours = minutes / 60
  if (hours < 24) return `${Math.floor(hours)} ч`
  const days = hours / 24
  if (days < 60) return `${Math.floor(days)} дн`
  return `${Math.floor(days / 30)} мес`
}

/** «5 минут назад», «вчера в 14:05» — для «последней проверки». */
export function formatAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'ещё не проверялся'
  const age = formatAge(iso, now)
  return age === 'только что' ? age : `${age} назад`
}

export function plural(n: number, one: string, few: string, many: string): string {
  const k = Math.abs(Math.trunc(n))
  if (k % 10 === 1 && k % 100 !== 11) return one
  if (k % 10 >= 2 && k % 10 <= 4 && (k % 100 < 12 || k % 100 > 14)) return few
  return many
}

export function formatTimecode(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = String(s % 60).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
}

const dateTime = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
})

export function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : dateTime.format(d)
}

export function initials(name: string): string {
  // Только слова с буквы: «Angelina | AI» → «AA», а не «A|».
  const parts = name
    .replace(/^@/, '')
    .trim()
    .split(/\s+/)
    .filter((p) => /^[\p{L}\p{N}]/u.test(p))
  const letters = parts.length > 1 ? [parts[0]?.[0], parts[1]?.[0]] : [parts[0]?.[0]]
  return letters.join('').toUpperCase() || '?'
}

export const PLATFORM_LABEL = { instagram: 'Instagram', telegram: 'Telegram', reddit: 'Reddit' } as const

/** @ник для Instagram и Telegram, r/… для Reddit. */
export function handleLabel(platform: string, handle: string): string {
  return platform === 'reddit' ? `r/${handle}` : `@${handle}`
}

/** Имя файла ролика, как у сервера: `ник_код.mp4`. */
export function postFileName(post: { id: number; url: string | null; author: { handle: string } }): string {
  const code = post.url?.match(/\/(?:p|reels?|tv)\/([A-Za-z0-9_-]+)/)?.[1] ?? String(post.id)
  return `${post.author.handle}_${code}`.replace(/[^\w.-]+/g, '_') + '.mp4'
}

/** Что вставить в поле «Новый источник», чтобы площадка не перепуталась. */
export function sourceInput(platform: string, handle: string): string {
  if (platform === 'telegram') return `t.me/${handle}`
  if (platform === 'reddit') return `r/${handle}`
  return `instagram.com/${handle}`
}

const usd = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** $0,70 — деньги Apify; круглые без копеек: $30. */
export function formatUsd(value: number): string {
  return `$${Number.isInteger(value) ? x0.format(value) : usd.format(value)}`
}

const shortDate = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' })

export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : shortDate.format(d)
}

/**
 * Доля просмотров от подписчиков автора: 3,5 — просмотров втрое больше, чем
 * подписчиков. Так же сортирует сервер (`share`). Подписчиков не знаем — null.
 */
export function followerShare(plays: number | null, followers: number | null): number | null {
  return plays !== null && followers ? plays / followers : null
}

/** «добавил Даниил · 23 окт.» — подпись плитки в «Добавленных». Участника убрали — «добавлен · …». */
export function addedLabel(manual: ManualInfo): string {
  const when = formatShortDate(manual.added_at)
  return `${manual.added_by_name ? `добавил ${manual.added_by_name}` : 'добавлен'} · ${when}`
}

// Расписание сбора владелец знает по московскому времени (09:05 и 21:05 МСК).
const mskTime = new Intl.DateTimeFormat('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit' })
const mskDay = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' })

/** «сегодня в 21:05 МСК», «завтра в 09:05 МСК» — ближайший обход. */
export function formatCheckTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const day = mskDay.format(d)
  const today = mskDay.format(new Date(now))
  const tomorrow = mskDay.format(new Date(now + 86_400_000))
  const when = day === today ? 'сегодня' : day === tomorrow ? 'завтра' : shortDate.format(d)
  return `${when} в ${mskTime.format(d)} МСК`
}
